import * as Yup from 'yup';
import { useEffect, useMemo } from 'react';
import { useSnackbar } from 'notistack';
import { useNavigate } from 'react-router-dom';
// form
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
// @mui
import { LoadingButton } from '@mui/lab';
import { Alert, Box, Button, Card, Grid, Stack } from '@mui/material';

// routes

import useLocales from 'src/hooks/useLocales';
// components

import { observer } from 'mobx-react-lite';
import { IElectricBox } from 'src/@types/Choqorak/Settings/ElectricBox';
import { FormProvider, RHFTextField } from 'src/components/hook-form';
import Iconify from 'src/components/Iconify';
import { PATH_DASHBOARD } from 'src/routes/paths';
import { useStore } from 'src/stores/store';
import useThrottle from 'src/hooks/useThrottle';
// ----------------------------------------------------------------------

export default observer(function ElectricBoxNewEditForm() {
  const { ElectricBoxStore } = useStore();
  const { translate } = useLocales();
  const {
    createElectricBox,
    updateElectricBox,
    editMode,
    selectedElectricBox,
    clearSelectedElectricBox,
  } = ElectricBoxStore;
  const navigate = useNavigate();

  const { enqueueSnackbar } = useSnackbar();

  const NewElectricBoxSchema = Yup.object().shape({
    electricBoxCode: Yup.string().required(`${translate('Validation.electricBoxCode')}`),
    description: Yup.string().required(`${translate('Validation.description')}`),
  });

  const defaultValues = useMemo<IElectricBox>(
    () => ({
      electricBoxId: selectedElectricBox?.electricBoxId,
      electricBoxCode: selectedElectricBox?.electricBoxCode || '',
      description: selectedElectricBox?.description || '',
    }),
    [selectedElectricBox]
  );

  const methods = useForm<IElectricBox>({
    resolver: yupResolver(NewElectricBoxSchema),
    defaultValues,
  });

  const {
    reset,
    handleSubmit,
    setError,
    formState: { isSubmitting, errors },
  } = methods;

  const onSubmit = useThrottle((data: IElectricBox) => {
    if (data.electricBoxId! === undefined) {
      ///create
      createElectricBox(data)
        .then(() => {
          reset();
          enqueueSnackbar(`${translate('Tostar.CreateSuccess')}`);
          navigate(PATH_DASHBOARD.ElectricBox.list);
        })
        .catch((err) => {
          var json = JSON.parse(err.request.response);
          if (json.error != null) {
            setError('afterSubmit', { ...err, message: json.message });
          }
        });
    } else {
      ///update
      updateElectricBox(data)
        .then(() => {
          reset();
          enqueueSnackbar(`${translate('Tostar.UpdateSuccess')}`);
          navigate(PATH_DASHBOARD.ElectricBox.list);
        })
        .catch((err) => {
          var json = JSON.parse(err.request.response);
          if (json.error != null) {
            setError('afterSubmit', { ...err, message: json.message });
          }
        });
    }
  });

  useEffect(() => {
    if (editMode) {
      reset(defaultValues);
    }
    if (!editMode) {
      reset(defaultValues);
    }
  }, [reset, editMode, defaultValues]);

  return (
    <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
      {!!errors.afterSubmit && (
        <Alert sx={{ mb: 2 }} severity="error">
          {errors.afterSubmit.message}
        </Alert>
      )}
      <Grid container spacing={3}>
        <Grid item xs={12} md={12}>
          <Card sx={{ p: 3 }}>
            <Box
              sx={{
                display: 'grid',
                columnGap: 2,
                rowGap: 3,
                gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
              }}
            >
              <RHFTextField
                name="electricBoxCode"
                label={translate('ElectricBox.ElectricBoxCode')}
                showAsterisk={true}
                autoFocus
              />
              <RHFTextField
                name="description"
                label={translate('ElectricBox.Description')}
                showAsterisk={true}
              />
            </Box>

            <Stack direction="row" spacing={1.5} sx={{ mt: 3 }}>
              <LoadingButton
                fullWidth
                type="submit"
                variant="contained"
                loading={isSubmitting}
                startIcon={
                  !editMode ? <Iconify icon="eva:plus-fill" /> : <Iconify icon="eva:edit-fill" />
                }
              >
                {!editMode ? `${translate('CRUD.Save')}` : `${translate('CRUD.Update')}`}
              </LoadingButton>
              <Button
                fullWidth
                variant="contained"
                color="error"
                startIcon={<Iconify icon="eva:arrow-ios-back-fill" />}
                onClick={() => {
                  clearSelectedElectricBox();
                  navigate(PATH_DASHBOARD.ElectricBox.list);
                }}
              >
                {translate('CRUD.BackToList')}
              </Button>
            </Stack>
          </Card>
        </Grid>
      </Grid>
    </FormProvider>
  );
});
